import { ReactElement } from 'react';

import { Box, InputBase, InputBaseProps } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { NumericFormat } from 'react-number-format';

interface ChargeInputProps extends InputBaseProps {
    label?: string;
    currency?: string;
    percentage?: boolean;
    min?: number;
    max?: number;
    endAdornment?: ReactElement;
    startAdornment?: ReactElement;
}
export const ChargeInput = ({
    placeholder,
    name,
    disabled = false,
    min,
    max,
    endAdornment,
    startAdornment,
}: ChargeInputProps) => {
    const [field, meta, helper] = useField(name);

    if (field.value === undefined) {
        // add blank space to align boxes to correct column
        return <Box display="flex" flexDirection="column" sx={{ height: '100%', width: '100%' }} />;
    }

    const formik = useFormikContext();

    const hasError = meta.touched && meta.error;
    const { errors } = formik;

    const validate = (value: number) => {
        let highestValue = max;
        let lowestValue = min;

        // Cater for user errors when making lowest higher than the highest allowed
        if (max < min) {
            highestValue = min;
            lowestValue = max;
        }

        if (value < lowestValue || value > highestValue) {
            return `Value must be between ${lowestValue} and ${highestValue}`;
        }
        return undefined; // No error
    };

    // track for invalid entries on change, but also convert the value to a number for the correct type
    const handleCurrencyChange = (e) => {
        e.preventDefault();
        const numberValue = Number(e.target.value);
        helper.setValue(numberValue);
        helper.setError(validate(numberValue));
    };

    // recheck entry on blur for validity, but also check the error object for valid paths (no errors)
    const handleBlurCurrency = () => {
        helper.setTouched(true);
        helper.setError(validate(field.value));
        resetErrors(errors);
    };

    // this function scans the error tree manually and removes parents of validation errors that have been corrected
    function resetErrors(obj) {
        for (const key in obj) {
            if (obj[key] && typeof obj[key] === 'object') {
                obj[key] = resetErrors(obj[key]);
                if (Object.keys(obj[key]).length === 0) {
                    delete obj[key];
                }
            } else if (obj[key] === undefined) {
                delete obj[key];
            }
        }

        return obj;
    }

    return (
        <Box display="flex" flexDirection="column" sx={{ height: '100%', width: '100%' }}>
            <NumericFormat
                name={name}
                value={field.value}
                defaultValue={0.0}
                onChange={handleCurrencyChange}
                onBlur={handleBlurCurrency}
                decimalSeparator="."
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                customInput={InputBase}
                placeholder={placeholder}
                autoComplete="off"
                className={`form-input ${hasError ? 'input-error' : ''}`}
                disabled={disabled}
                endAdornment={endAdornment}
                startAdornment={startAdornment}
                style={{
                    height: '100%',
                }}
            />
            {!disabled && hasError ? (
                <label
                    className="input-base-field-error"
                    style={{
                        wordWrap: 'break-word',
                        maxWidth: '100%',
                        display: 'flex',
                        position: 'relative',
                        bottom: 0,
                    }}
                >
                    {/* Display an error message for value out of range */}
                    {meta.error}
                </label>
            ) : null}
        </Box>
    );
};

export { InputBase };
